import React, { useState } from 'react';
import { ArrowRight, Shield, Users, Monitor, FileCheck, Leaf, CheckCircle2, TreePine, Cone } from 'lucide-react';
import { Button } from './ui/button';
import { heroData, trustBadges, images } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const trustIcons = {
  1: Shield,
  2: Users,
  3: Monitor,
  4: FileCheck,
};

// Floating leaf component
const FloatingLeaf = ({ delay, left, duration, icon: Icon, className = '', sizeClass = 'w-6 h-6', startOffset = '-12%' }) => (
  <div
    className={`absolute text-white/10 animate-leaf-fall ${className}`}
    style={{
      left: `${left}%`,
      top: startOffset,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
    }}
  >
    <Icon className={sizeClass} />
  </div>
);

const Hero = () => {
  const [isVisible] = useState(true);
  const { t } = useLanguage();

  const heroCopy = t('hero', heroData || {});
  const trustBadgesTranslated = t('trustBadges', trustBadges || []);

  const forestIcons = [TreePine, Leaf, Cone];

  const floatingLeaves = Array.from({ length: 6 }, (_, index) => ({
    id: `forest-${index + 1}`,
    delay: index * 2.5,
    left: 8 + index * 14,
    duration: 12 + index * 2,
    icon: forestIcons[index % forestIcons.length],
    className: '',
    sizeClass: index % 3 === 0 ? 'w-7 h-7' : 'w-6 h-6',
    startOffset: `-${12 + index * 3}%`
  }));

  const salesHighlights = heroCopy.salesHighlights || heroData.salesHighlights || [
    'Neatkarīgs vērtējums',
    'Precīzi uzmērījumi',
    'Pilna dokumentācija',
    'Pilnīga caurspīdība'
  ];

  const subtitleHighlight = t('hero.subtitleHighlight', 'precīzi novērtēt cirsmas vērtību');
  const descriptionHighlight = t('hero.descriptionHighlight', 'nepērkam cirsmas un neesam saistīti ar koksnes pircējiem.');

  const renderSubtitle = () => {
    const subtitleText = heroCopy.subtitle || heroData.subtitle;
    if (!subtitleText?.includes(subtitleHighlight)) {
      return subtitleText;
    }
    const [before, after] = subtitleText.split(subtitleHighlight);
    return (
      <>
        {before}
        <span className="forest-highlight">{subtitleHighlight}</span>
        {after}
      </>
    );
  };

  const renderDescription = () => {
    const descriptionText = heroCopy.description || heroData.description;
    if (!descriptionText?.includes(descriptionHighlight)) {
      return descriptionText;
    }
    const [before, after] = descriptionText.split(descriptionHighlight);
    return (
      <>
        {before}
        <span className="forest-highlight">{descriptionHighlight}</span>
        {after}
      </>
    );
  };

  return (
    <section id="sakums" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.hero}
          alt={t('hero.imageAlt', 'Latvijas mežs')}
          className="w-full h-full object-cover scale-105 animate-pulse-slow"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2a1c]/95 via-[#1F3D2B]/85 to-[#274e36]/70" />
        <div className="absolute inset-0 forest-mist" />
        <div className="absolute inset-0 forest-vignette" />
        
        {/* Animated overlay pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 forest-pattern" />
        </div>
      </div>

      {/* Floating leaves */}
      <div className="absolute inset-0 z-5 overflow-hidden pointer-events-none">
        {floatingLeaves.map((leaf) => (
          <FloatingLeaf
            key={leaf.id}
            delay={leaf.delay}
            left={leaf.left}
            duration={leaf.duration}
            icon={leaf.icon}
            className={leaf.className}
            sizeClass={leaf.sizeClass}
            startOffset={leaf.startOffset}
          />
        ))}
      </div>

      {/* Decorative blobs - hidden on mobile to prevent horizontal overflow */}
      <div className="hidden sm:block absolute top-20 right-10 w-72 h-72 bg-green-400/10 rounded-full blur-3xl animate-blob" />
      <div className="hidden sm:block absolute bottom-20 left-10 w-96 h-96 bg-green-300/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
      <div className="hidden sm:block absolute top-28 left-1/3 w-48 h-3 forest-branch animate-sway" style={{ animationDelay: '1s' }} />
      <div className="absolute bottom-0 left-0 w-full h-32 forest-canopy" />
      <div className="hidden sm:block absolute bottom-16 right-16 w-40 h-40 tree-ring opacity-30" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 pt-32 sm:pt-40">
        <div className="max-w-4xl mx-auto sm:mx-0 text-center sm:text-left">
          {/* Badge */}
          <div 
            className={`inline-flex items-center gap-2 forest-badge px-4 py-2 rounded-full mb-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            data-testid="hero-badge"
          >
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-white/90 text-sm font-medium" data-testid="hero-badge-text">
              {t('heroBadge', 'Neatkarīgi mežsaimniecības pakalpojumi')}
            </span>
          </div>

          {/* Main Heading */}
          <h1 
            className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6 transition-all duration-700 delay-100 forest-title ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-title"
          >
            {heroCopy.title || heroData.title}
          </h1>

          {/* Subtitle */}
          <p 
            className={`text-base sm:text-lg text-white/85 leading-relaxed mb-6 max-w-3xl transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-subtitle"
          >
            {renderSubtitle()}
          </p>

          {/* Description */}
          <p 
            className={`text-base sm:text-lg text-white/75 leading-relaxed mb-6 max-w-3xl transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-description"
          >
            {renderDescription()}
          </p>

          {/* Sales Highlights */}
          <div
            className={`flex flex-wrap gap-3 mb-6 justify-center sm:justify-start transition-all duration-700 delay-325 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-sales-highlights"
          >
            {salesHighlights.map((item, index) => (
              <span
                key={item}
                className="forest-chip animate-float"
                style={{ animationDelay: `${index * 0.4}s` }}
                data-testid={`hero-sales-chip-${index}`}
              >
                {item}
              </span>
            ))}
          </div>

          <div
            className={`forest-callout mb-8 text-center sm:text-left transition-all duration-700 delay-350 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-sales-callout"
          >
            <span className="text-white/80 text-xs uppercase tracking-[0.2em]">
              {heroCopy.calloutTitle || t('hero.calloutTitle', 'Sila Meži garantija')}
            </span>
            <p className="text-white text-base sm:text-lg font-semibold mt-2">
              {heroCopy.calloutBody || t('hero.calloutBody', 'Precīzi mērījumi. Korekta dokumentācija. Pilnīga caurspīdība.')}
            </p>
          </div>

          {/* Work + Reasons */}
          <div 
            className={`grid lg:grid-cols-2 gap-6 mb-8 transition-all duration-700 delay-350 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-highlights"
          >
            <div className="bg-white/10 border border-white/10 rounded-2xl p-6 backdrop-blur-sm text-left" data-testid="hero-work-card">
              <h3 className="text-white font-semibold text-lg mb-4" data-testid="hero-work-title">
                {heroCopy.workIntro || heroData.workIntro}
              </h3>
              <ul className="space-y-3" data-testid="hero-work-list">
                {(heroCopy.workItems || heroData.workItems || []).map((item, index) => (
                  <li key={item} className="flex items-start gap-3 text-white/85 text-sm" data-testid={`hero-work-item-${index}`}>
                    <CheckCircle2 className="w-4 h-4 text-green-300 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-sm text-white/70" data-testid="hero-work-note">
                {heroCopy.workNote || heroData.workNote}
              </div>
              <a
                href="#darbi"
                className="inline-flex items-center text-sm text-green-200 font-semibold mt-3 hover:text-green-100"
                data-testid="hero-work-link"
              >
                {heroCopy.workLink || t('hero.workLink', 'Skatīt darbus')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
            <div className="bg-white/10 border border-white/10 rounded-2xl p-6 backdrop-blur-sm text-left" data-testid="hero-reasons-card">
              <h3 className="text-white font-semibold text-lg mb-4" data-testid="hero-reasons-title">
                {heroCopy.reasonsTitle || heroData.reasonsTitle}
              </h3>
              <ul className="space-y-3" data-testid="hero-reasons-list">
                {(heroCopy.reasons || heroData.reasons || []).map((reason, index) => (
                  <li key={reason} className="flex items-start gap-3 text-white/85 text-sm" data-testid={`hero-reason-item-${index}`}>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-300 mt-2 flex-shrink-0" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p 
            className={`text-base sm:text-lg text-white/80 leading-relaxed mb-4 max-w-3xl transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-closing"
          >
            {heroCopy.closing || heroData.closing}
          </p>

          {/* Tagline */}
          <p 
            className={`text-lg sm:text-xl text-white font-semibold mb-10 transition-all duration-700 delay-450 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            data-testid="hero-tagline"
          >
            <span className="relative">
              {heroCopy.tagline || heroData.tagline}
              <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-green-400/50" />
            </span>
          </p>

          {/* CTA Buttons */}
          <div 
            className={`flex flex-col sm:flex-row gap-4 mb-16 items-center sm:items-start transition-all duration-700 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <Button
              size="lg"
              className="bg-white text-[#1F3D2B] hover:bg-white/90 font-semibold px-8 py-6 text-base shadow-2xl shadow-black/20 hover:shadow-black/30 hover:scale-105 transition-all duration-300"
              asChild
            >
              <a href="#kontakti" data-testid="hero-cta-primary">
                {heroCopy.ctaPrimary || heroData.ctaPrimary}
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-6 text-base backdrop-blur-sm hover:scale-105 transition-all duration-300"
              asChild
            >
              <a href="#portals" data-testid="hero-cta-secondary">
                {heroCopy.ctaSecondary || heroData.ctaSecondary}
              </a>
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {trustBadgesTranslated.map((badge, index) => {
              const IconComponent = trustIcons[badge.id];
              return (
                <div
                  key={badge.id}
                  className={`bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/20 hover:border-white/20 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl cursor-default ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${600 + index * 100}ms` }}
                  data-testid={`hero-trust-badge-${badge.id}`}
                >
                  <IconComponent className="w-8 h-8 text-green-400 mb-3 transition-transform duration-300 group-hover:scale-110" />
                  <h3 className="text-white font-semibold text-sm mb-1" data-testid={`hero-trust-title-${badge.id}`}>
                    {badge.title}
                  </h3>
                  <p className="text-white/60 text-xs" data-testid={`hero-trust-description-${badge.id}`}>{badge.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center hover:border-white/50 transition-colors duration-300">
          <div className="w-1.5 h-3 bg-white/60 rounded-full mt-2 animate-bounce" />
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 60V30C240 50 480 10 720 30C960 50 1200 10 1440 30V60H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
